import React, { useState, useEffect } from "react";
import { Layers, Loader2, Plus, Star, RefreshCw, Trash2, Play, Sparkles, Pencil, TrendingUp, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { FlashcardReview } from "@/components/learning/FlashcardReview";
import { flashcardsApi } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/context/LanguageContext";
import { toast } from "sonner";

const TYPE_KEYS = ["basic", "concept", "fill_blank", "reasoning"];
const DIFF_KEYS = ["beginner", "intermediate", "advanced"];
const STATE_COLOR = { new: "hsl(215,12%,55%)", learning: "hsl(38,92%,58%)", familiar: "hsl(206,70%,52%)", mastered: "hsl(152,52%,46%)" };

export const FlashcardDeck = ({ docId, onChanged, api }) => {
    const { t } = useTranslation();
    const [cards, setCards] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [count, setCount] = useState(12);
    const [types, setTypes] = useState(["basic", "concept", "fill_blank", "reasoning"]);
    const [diff, setDiff] = useState("intermediate");
    const [reviewing, setReviewing] = useState(false);
    const [edit, setEdit] = useState(null);
    const showFromWeak = !api || !!api.fromWeak;

    const load = async () => {
        try { setCards(await (api?.list ? api.list() : flashcardsApi.list(docId))); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [docId, api]);

    const toggleType = (ty) => setTypes((cur) => cur.includes(ty) ? cur.filter((x) => x !== ty) : [...cur, ty]);

    const generate = async () => {
        if (!types.length) { toast.error(t("deck.pickType")); return; }
        setGenerating(true);
        try {
            const opts = { count, types, difficulty: diff };
            const created = await (api?.generate ? api.generate(opts) : flashcardsApi.generate(docId, opts));
            setCards((c) => [...(c || []), ...created]);
            toast.success(t("deck.created", { n: created.length }));
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("deck.genFailed"));
        } finally { setGenerating(false); }
    };

    const toggleImportant = async (card) => {
        const next = !card.important;
        setCards((cs) => cs.map((c) => c.id === card.id ? { ...c, important: next } : c));
        try { await flashcardsApi.update(card.id, { important: next }); } catch (e) {}
    };
    const regenerate = async (card, mode = "improve") => {
        try {
            const updated = await flashcardsApi.regenerate(card.id, mode);
            setCards((cs) => cs.map((c) => c.id === card.id ? updated : c));
            toast.success(t("deck.regenerated"));
        } catch (e) { toast.error(t("deck.regenFailed")); }
    };
    const fromWeak = async () => {
        setGenerating(true);
        try {
            const created = await flashcardsApi.fromTopics(docId, { count: 8, difficulty: "exam" });
            setCards((c) => [...(c || []), ...created]);
            toast.success(t("pro.weakGenerated", { n: created.length }));
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.status === 400 ? t("pro.noWeak") : (e?.response?.data?.detail || t("deck.genFailed")));
        } finally { setGenerating(false); }
    };
    const remove = async (card) => {
        setCards((cs) => cs.filter((c) => c.id !== card.id));
        try { await flashcardsApi.remove(card.id); onChanged?.(); } catch (e) {}
    };
    const saveEdit = async () => {
        try {
            const updated = await flashcardsApi.update(edit.id, { front: edit.front, back: edit.back, hint: edit.hint || null });
            setCards((cs) => cs.map((c) => c.id === edit.id ? updated : c));
            setEdit(null); toast.success(t("deck.saved"));
        } catch (e) { toast.error(t("deck.saveFailed")); }
    };
    const onRate = async (card, rating) => {
        const updated = await flashcardsApi.review(card.id, rating);
        setCards((cs) => cs.map((c) => c.id === card.id ? { ...c, ...updated } : c));
        onChanged?.();
    };

    if (loading) {
        return <div className="grid gap-3 sm:grid-cols-2">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-28 rounded-2xl bg-surface-2" />)}</div>;
    }

    if (reviewing && cards?.length) {
        return (
            <div className="mx-auto max-w-xl">
                <Button variant="ghost" size="sm" className="mb-3 text-t3" onClick={() => { setReviewing(false); }}>← {t("deck.backToDeck")}</Button>
                <FlashcardReview cards={cards} onRate={onRate} onDone={() => { setReviewing(false); toast.success(t("deck.reviewComplete")); }} />
            </div>
        );
    }

    const hasCards = cards && cards.length > 0;

    return (
        <div data-testid="flashcard-deck">
            {/* Generator */}
            <div className="rounded-2xl border border-border bg-surface-1 p-5">
                <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-[hsl(var(--primary))]" />
                    <h3 className="font-sans-display text-sm font-semibold text-t1">{hasCards ? t("deck.generateMore") : t("deck.generate")}</h3>
                </div>
                <div className="mt-4 flex flex-wrap items-end gap-4">
                    <div>
                        <div className="mono-label mb-2 text-t4">{t("deck.cardTypes")}</div>
                        <div className="flex flex-wrap gap-1.5">
                            {TYPE_KEYS.map((ty) => (
                                <button key={ty} data-testid={`fc-type-${ty}`} onClick={() => toggleType(ty)}
                                    className={cn("rounded-lg px-2.5 py-1.5 text-xs transition-colors", types.includes(ty) ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "bg-surface-2 text-t3 hover:text-t1")}>{t(`deck.types.${ty}`)}</button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className="mono-label mb-2 text-t4">{t("deck.difficulty")}</div>
                        <div className="flex gap-1.5">
                            {DIFF_KEYS.map((d) => (
                                <button key={d} data-testid={`fc-diff-${d}`} onClick={() => setDiff(d)}
                                    className={cn("rounded-lg px-2.5 py-1.5 text-xs transition-colors", diff === d ? "bg-surface-3 text-t1" : "bg-surface-2 text-t3")}>{t(`deck.diffs.${d}`)}</button>
                            ))}
                        </div>
                    </div>
                    <div className="min-w-[140px]">
                        <div className="mono-label mb-2 text-t4">{t("deck.count")}: {count}</div>
                        <Slider value={[count]} min={4} max={24} step={1} onValueChange={(v) => setCount(v[0])} />
                    </div>
                    <Button data-testid="fc-generate-button" className="gap-2" disabled={generating} onClick={generate}>
                        {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("deck.generateBtn")}
                    </Button>
                    {showFromWeak && (
                        <Button data-testid="fc-from-weak-button" variant="secondary" className="gap-2 bg-surface-3/60" disabled={generating} onClick={fromWeak} title={t("pro.fromWeakDesc")}>
                            <Target className="h-4 w-4" /> {t("pro.fromWeak")}
                        </Button>
                    )}
                </div>
            </div>

            {hasCards && (
                <>
                    <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-t2">
                            <Layers className="h-4 w-4 text-[hsl(var(--primary))]" />
                            <span className="font-sans-display text-sm font-semibold">{cards.length} {t("deck.cards")}</span>
                            <div className="ml-2 flex items-center gap-2">
                                {Object.keys(STATE_COLOR).map((s) => {
                                    const n = cards.filter((c) => c.state === s).length;
                                    return n ? <span key={s} className="inline-flex items-center gap-1 text-xs text-t4"><span className="h-2 w-2 rounded-full" style={{ background: STATE_COLOR[s] }} />{n}</span> : null;
                                })}
                            </div>
                        </div>
                        <Button data-testid="fc-start-review-button" className="gap-2" onClick={() => setReviewing(true)}><Play className="h-4 w-4" /> {t("deck.studyThese")}</Button>
                    </div>

                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        {cards.map((c) => (
                            <div key={c.id} data-testid="flashcard-item" className="group rounded-2xl border border-border bg-surface-1 p-4 transition-colors hover:bg-surface-2">
                                <div className="flex items-start justify-between gap-2">
                                    <span className="rounded-full bg-surface-2 px-2 py-0.5 text-[10px] uppercase text-t4">{t(`deck.types.${c.type}`)}</span>
                                    <div className="flex items-center gap-0.5 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                                        <button title={t("deck.editTitle")} onClick={() => toggleImportant(c)} className="rounded p-1 text-t4 hover:text-[hsl(var(--warning))]"><Star className={cn("h-3.5 w-3.5", c.important && "fill-[hsl(var(--warning))] text-[hsl(var(--warning))]")} /></button>
                                        <button title={t("deck.editTitle")} onClick={() => setEdit({ ...c })} className="rounded p-1 text-t4 hover:text-t1"><Pencil className="h-3.5 w-3.5" /></button>
                                        <button title={t("pro.improve")} onClick={() => regenerate(c, "improve")} className="rounded p-1 text-t4 hover:text-[hsl(var(--primary))]"><RefreshCw className="h-3.5 w-3.5" /></button>
                                        <button data-testid="fc-harder-button" title={t("pro.harder")} onClick={() => regenerate(c, "harder")} className="rounded p-1 text-t4 hover:text-[hsl(38,92%,58%)]"><TrendingUp className="h-3.5 w-3.5" /></button>
                                        <button title={t("common.delete")} onClick={() => remove(c)} className="rounded p-1 text-t4 hover:text-[hsl(var(--destructive))]"><Trash2 className="h-3.5 w-3.5" /></button>
                                    </div>
                                </div>
                                <p className="mt-2 text-sm font-medium text-t1">{c.front}</p>
                                <p className="mt-1.5 font-reading text-[14px] leading-relaxed text-t3">{c.back}</p>
                            </div>
                        ))}
                    </div>
                </>
            )}

            <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
                <DialogContent className="border-border bg-surface-1">
                    <DialogHeader><DialogTitle className="text-t1">{t("deck.editTitle")}</DialogTitle></DialogHeader>
                    {edit && (
                        <div className="space-y-3">
                            <div><div className="mono-label mb-1 text-t4">{t("deck.front")}</div><Textarea value={edit.front} onChange={(e) => setEdit({ ...edit, front: e.target.value })} className="bg-surface-2" /></div>
                            <div><div className="mono-label mb-1 text-t4">{t("deck.back")}</div><Textarea value={edit.back} onChange={(e) => setEdit({ ...edit, back: e.target.value })} className="bg-surface-2" /></div>
                        </div>
                    )}
                    <DialogFooter><Button variant="secondary" className="bg-surface-3" onClick={() => setEdit(null)}>{t("common.cancel")}</Button><Button onClick={saveEdit}>{t("deck.saved")}</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
